/**
 * media:// reference resolution (specs/changes/photo-capture Phase 4).
 *
 * Documents store captured images as stable `media://{mediaId}` references —
 * never expiring URLs. At render time the console swaps each reference for a
 * short-lived presigned URL from the authenticated GET /media/{id} endpoint
 * (tenant-owned media only). Resolutions are cached until shortly before
 * expiry; a failed resolution leaves the reference untouched so the document
 * text is never corrupted.
 */

import { mediaApi } from '@/services/mediaApi';

const MEDIA_REF = /media:\/\/([A-Za-z0-9_-]+)/g;
const EXPIRY_MARGIN_MS = 30_000;

interface CachedUrl {
  url: string;
  expiresAt: number;
}

const cache = new Map<string, CachedUrl>();

type Resolver = (mediaId: string) => Promise<{ url: string; expiresIn: number }>;

/** Whether content contains any media:// reference (cheap pre-check). */
export function hasMediaReferences(content: string): boolean {
  return content.includes('media://');
}

/** Test seam: clear the resolution cache. */
export function clearMediaCache(): void {
  cache.clear();
}

/**
 * Replace every media:// reference with a short-lived download URL.
 * Unknown/unauthorized ids resolve to nothing and are left as-is.
 */
export async function resolveMediaReferences(
  content: string,
  resolve: Resolver = (id) => mediaApi.getMedia(id),
): Promise<string> {
  const ids = [...new Set([...content.matchAll(MEDIA_REF)].map((m) => m[1]))];
  if (ids.length === 0) return content;

  const now = Date.now();
  const resolved = new Map<string, string>();
  await Promise.all(ids.map(async (id) => {
    const cached = cache.get(id);
    if (cached && cached.expiresAt - EXPIRY_MARGIN_MS > now) {
      resolved.set(id, cached.url);
      return;
    }
    try {
      const media = await resolve(id);
      cache.set(id, { url: media.url, expiresAt: now + media.expiresIn * 1000 });
      resolved.set(id, media.url);
    } catch {
      // Leave the reference untouched — cross-tenant/deleted media renders
      // as a broken image, never someone else's bytes.
    }
  }));

  return content.replace(MEDIA_REF, (whole, id) => resolved.get(id) ?? whole);
}
